// Client custom-field applicability (QL-213).
//
// `custom_field.clientType` holds either a client type or a sentinel meaning
// "every client type". The rule "show this field when it applies to all types,
// or when it matches this client's type" was written out by hand at three call
// sites plus a dead fourth, and the value union was declared three times; both
// live here now, which is what made the sentinel rename one edit rather than
// five.
//
// The sentinel is `all`. It was `both` — a word meaning *two*, chosen before the
// Group client type existed, though it always matched all three. Renamed in one
// clean release with no alias: `both` is not accepted on write, not tolerated on
// read, and survives only inside migration 0114.
//
// NOT the same concept as `clientTypeAllowed` in ./client-types — that is a
// list-based allow-list for flows and goal types.

import { ALL_CLIENT_TYPES } from "./client-types";

/** The sentinel meaning "this field applies to every client type". */
export const CLIENT_FIELD_APPLIES_TO_ALL = "all";

/**
 * Every value `custom_field.clientType` may hold. Derived from the client-type
 * list, so a new client type becomes a valid applicability value automatically.
 */
export const CLIENT_FIELD_APPLICABILITIES = [
  CLIENT_FIELD_APPLIES_TO_ALL,
  ...ALL_CLIENT_TYPES,
] as const;

export type ClientFieldApplicability = (typeof CLIENT_FIELD_APPLICABILITIES)[number];

/**
 * Narrow an arbitrary string to a known applicability value. The column is a
 * plain varchar with no CHECK, so anything can be in there; the write path uses
 * this to reject values that would make a field apply to nobody.
 */
export function isClientFieldApplicability(value: string): value is ClientFieldApplicability {
  return (CLIENT_FIELD_APPLICABILITIES as readonly string[]).includes(value);
}

/**
 * Does a field with this applicability apply to a client of this type? The
 * all-types sentinel matches every client, including ones whose type is unset;
 * anything else matches only its own type. An unrecognised applicability value
 * matches nothing — deliberately, so a bad value is visible as a missing field
 * rather than silently treated as "applies to all".
 */
export function fieldAppliesToClient(
  applicability: string | null | undefined,
  clientType: string | null | undefined,
): boolean {
  if (applicability === CLIENT_FIELD_APPLIES_TO_ALL) return true;
  return applicability === clientType;
}

/**
 * The effective "Visible in List" flag for a field with this applicability. The
 * client table has one column per list-visible field, so a column that is blank
 * for every Company client is noise — the flag is only available to a field that
 * applies to every client type, and is forced off for anything else, including
 * an unrecognised value (a field that applies to nobody must not occupy a
 * column). When no flag is submitted a new field is list-visible; callers that
 * must preserve an existing value pass it in rather than relying on that.
 */
export function resolveFieldListVisibility(
  applicability: string | null | undefined,
  requested: boolean | undefined,
): boolean {
  if (applicability !== CLIENT_FIELD_APPLIES_TO_ALL) return false;
  return requested ?? true;
}
